import { createFileRoute, useParams } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpc } from "~/utils/trpc";
import { useState, useEffect } from "react";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Input } from "@quake/shared-ui/components/ui/input";
import { Label } from "@quake/shared-ui/components/ui/label";
import { Switch } from "@quake/shared-ui/components/ui/switch";
import { ColorPicker } from "@quake/shared-ui/components/ui/color-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@quake/shared-ui/components/ui/select";
import type { CandidateStatusType } from "@quake/isomorphic";
import { StatusTypeSelect } from "~/components/base/StatusTypeSelect";
import { CANDIDATE_STATUS_TYPE_OPTIONS } from "~/lib/statusTypes";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import PlusIcon from "lucide-react/dist/esm/icons/plus";
import EditIcon from "lucide-react/dist/esm/icons/edit";
import { Alert, AlertDescription } from "@quake/shared-ui/components/ui/alert";
import { toast } from "@quake/shared-ui/lib/sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@quake/shared-ui/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@quake/shared-ui/components/ui/table";
import type { AttrType } from "@quake/isomorphic/dataFilters";
import { makeAttributeId } from "@quake/isomorphic";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { AttributesDialog } from "~/components/AttributesDialog";
import { Separator } from "@quake/shared-ui/components/ui/separator";
import {
  DEFAULT_CANDIDATE_ACTIVE_STATUSES,
  type CandidateActiveStatusOption,
} from "@quake/isomorphic/schemas/candidates.internal.schema";
import { DeleteConfirmationDialog } from "~/components/DeleteConfirmationDialog";

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/candidates/settings")({
  component: RouteComponent,
});

let statusIdCounter = 0;
const createStatusId = () => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${statusIdCounter++}`;

function RouteComponent() {
  const { data: session, isLoading: isSessionLoading } = useQuery(trpc.auth.getSession.queryOptions());
  const { data: settings } = useQuery(trpc.candidatesSettings.get.queryOptions());
  const update = useMutation(trpc.candidatesSettings.update.mutationOptions());
  const [attributes, setAttributes] = useState<AttrType[]>([]);
  const [statuses, setStatuses] = useState<CandidateActiveStatusOption[]>([]);
  const [defaultStatusId, setDefaultStatusId] = useState<string | null>(null);
  const [automaticAiMemoryUpdatesEnabled, setAutomaticAiMemoryUpdatesEnabled] = useState(true);
  const [newStatus, setNewStatus] = useState({
    name: "",
    description: "",
    color: "#2196F3",
    type: "active" as CandidateStatusType,
  });
  const qc = useQueryClient();

  const attr: AttrType = {
    id: "",
    name: "",
    description: "",
    searchable: false,
    type: "Text",
    table_reference: "",
    options: [] as { value: string; label: string; index: number }[],
    defaultValue: "",
    boundaries: { required: false, unique: false },
  };

  const entityTypes = [
    "Text",
    "Number",
    "Checkbox",
    "Date",
    "TimeStamp",
    "Select",
    "Multi Select",
    "Record",
    "Multi Record",
  ] as const;
  const optionsList = ["Select", "Multi Select"] as const;

  const [attributeDialogOpen, setAttributeDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit">("add");
  const [selectedAttribute, setSelectedAttribute] = useState(attr);
  const [attrIndex, setAttrIndex] = useState(-1);
  const [attributeToDeleteIndex, setAttributeToDeleteIndex] = useState<number | null>(null);
  const { datasetSlug } = useParams({ from: "/_dashboard/$datasetSlug" });
  const canCreateAttributes = session?.user?.role === "Administrator";

  useEffect(() => {
    setAttributes(settings?.attributes || []);
    const nextStatuses = settings?.statuses ?? DEFAULT_CANDIDATE_ACTIVE_STATUSES;
    setStatuses(nextStatuses);
    setDefaultStatusId(settings?.default_status_id || nextStatuses[0]?.id || null);
    setAutomaticAiMemoryUpdatesEnabled(settings?.automatic_ai_memory_updates_enabled ?? true);
  }, [settings]);

  useBreadCrumbs([
    { title: "Candidates", link: `/${datasetSlug}/data/candidates` },
    { title: "Settings", link: `/${datasetSlug}/data/candidates/settings` },
  ]);

  const save = (
    attrs: typeof attributes,
    statuses: CandidateActiveStatusOption[],
    defaultStatusId: string | null,
    automaticMemoryUpdatesEnabled: boolean,
  ) =>
    update.mutate(
      {
        attributes: attrs,
        statuses: statuses.map(({ description, ...status }) => ({
          ...status,
          description: description ?? undefined,
        })),
        default_status_id: defaultStatusId,
        automatic_ai_memory_updates_enabled: automaticMemoryUpdatesEnabled,
      },
      {
        onSuccess: async () => {
          await Promise.all([
            qc.invalidateQueries(trpc.candidatesSettings.get.queryFilter()),
            qc.invalidateQueries({
              queryKey: trpc.users.getTablePreferences.queryOptions({ tableName: "recruitment_candidates" }).queryKey,
            }),
            qc.invalidateQueries({ queryKey: trpc.data.getRecruitmentCandidates.queryKey() }),
            qc.invalidateQueries({ queryKey: trpc.data.getRecruitmentCandidatesCount.queryKey() }),
            qc.invalidateQueries({
              queryKey: trpc.entityViews.getViews.queryKey({ entityType: "recruitment_candidates" }),
            }),
            qc.invalidateQueries({ queryKey: trpc.entityViews.getViewBySlug.queryKey() }),
            qc.invalidateQueries({
              queryKey: trpc.entityViews.getAvailableFields.queryKey({ entityType: "recruitment_candidates" }),
            }),
          ]);
          toast.success("Candidate settings saved successfully");
        },
      },
    );

  const handleAddClick = () => {
    setDialogMode("add");
    setSelectedAttribute(attr);
    setAttributeDialogOpen(true);
  };

  const handleEditClick = (a: typeof attr, idx: number) => {
    setDialogMode("edit");
    setSelectedAttribute(a);
    setAttrIndex(idx);
    setAttributeDialogOpen(true);
  };

  const handleDialogSave = (newAttr: typeof attr) => {
    let list = [...attributes];
    if (dialogMode === "edit" && attrIndex !== -1) {
      list = list.filter((_, idx) => idx !== attrIndex);
    }
    list = [...list, { ...newAttr }];
    setAttributes(list);
    setSelectedAttribute(attr);
    setAttrIndex(-1);
    setAttributeDialogOpen(false);
  };

  const handleDialogCancel = () => {
    setSelectedAttribute(attr);
    setAttrIndex(-1);
    setAttributeDialogOpen(false);
  };

  const removeAttribute = (index: number) => {
    setAttributeToDeleteIndex(index);
  };

  const confirmDeleteAttribute = () => {
    if (attributeToDeleteIndex !== null) {
      setAttributes((prev) => prev.filter((_, i) => i !== attributeToDeleteIndex));
      setAttributeToDeleteIndex(null);
    }
  };

  const updateStatus = (index: number, data: Partial<CandidateActiveStatusOption>) => {
    setStatuses((prev) => {
      const list = [...prev];
      list[index] = { ...list[index], ...data } as CandidateActiveStatusOption;
      return list;
    });
  };

  const removeStatus = (index: number) => {
    setStatuses((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (prev[index]?.id === defaultStatusId) setDefaultStatusId(next[0]?.id || null);
      return next;
    });
  };

  const addStatus = () => {
    if (!newStatus.name) return;
    const id = createStatusId();
    setStatuses([
      ...statuses,
      {
        id,
        name: newStatus.name,
        description: newStatus.description,
        color: newStatus.color,
        type: newStatus.type,
      },
    ]);
    if (!defaultStatusId) setDefaultStatusId(id);
    setNewStatus({ name: "", description: "", color: "#2196F3", type: "active" as CandidateStatusType });
  };

  return (
    <div className="space-y-4 mx-5 pb-8">
      <h1 className="text-2xl font-bold">Candidates Settings</h1>

      <Card>
        <CardHeader>
          <CardTitle>AI Memory</CardTitle>
          <CardDescription>
            Control automatic candidate AI memory updates after profile, note, document, resume intake, or placement
            changes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4 rounded-md border p-3">
            <div className="space-y-1">
              <Label htmlFor="automatic-ai-memory-updates">Automatic AI memory updates</Label>
              <p className="text-sm text-muted-foreground">
                When enabled, Quake rebuilds candidate AI memory after meaningful candidate data changes.
              </p>
            </div>
            <Switch
              id="automatic-ai-memory-updates"
              checked={automaticAiMemoryUpdatesEnabled}
              onCheckedChange={setAutomaticAiMemoryUpdatesEnabled}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Statuses</CardTitle>
          <CardDescription>
            Candidate availability statuses define their current job-seeking state. These appear as a colored dot next
            to the candidate's name in the table and on their profile.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mt-4">
            <Input
              value={newStatus.name}
              onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })}
              placeholder="New status"
              className="w-32"
            />
            <Input
              value={newStatus.description}
              onChange={(e) => setNewStatus({ ...newStatus, description: e.target.value })}
              placeholder="Description"
            />
            <ColorPicker
              selectedColor={newStatus.color}
              onColorChange={(color) => setNewStatus({ ...newStatus, color })}
            />
            <StatusTypeSelect
              value={newStatus.type}
              options={CANDIDATE_STATUS_TYPE_OPTIONS}
              onChange={(type) => setNewStatus({ ...newStatus, type })}
            />
            <Button onClick={addStatus}>Add</Button>
          </div>
          <ul className="space-y-2 mt-4">
            {statuses.map((s, i) => (
              <li key={s.id} className="flex items-center gap-2">
                <ColorPicker selectedColor={s.color} onColorChange={(color) => updateStatus(i, { color })} />
                <Input value={s.name} onChange={(e) => updateStatus(i, { name: e.target.value })} className="w-32" />
                <Input value={s.description || ""} onChange={(e) => updateStatus(i, { description: e.target.value })} />
                <StatusTypeSelect
                  value={s.type}
                  options={CANDIDATE_STATUS_TYPE_OPTIONS}
                  onChange={(type) => updateStatus(i, { type })}
                />
                <Trash2 className="h-4 w-4 cursor-pointer" onClick={() => removeStatus(i)} />
              </li>
            ))}
          </ul>
          <div className="mt-4">
            <Label>Default Candidate Status</Label>
            <Select value={defaultStatusId || undefined} onValueChange={(value) => setDefaultStatusId(value || null)}>
              <SelectTrigger className="w-60">
                <SelectValue placeholder="Select default candidate status" />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((status) => (
                  <SelectItem key={status.id} value={status.id}>
                    {status.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Custom Attributes</CardTitle>
          <CardDescription>
            Configure additional fields for candidates. These attributes will appear in the candidate form.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {canCreateAttributes ? (
            <div className="text-right">
              <Button type="button" onClick={handleAddClick}>
                <PlusIcon className="mr-1 h-4 w-4" /> Add Attribute
              </Button>
            </div>
          ) : isSessionLoading ? (
            <Alert>
              <AlertDescription>Loading session information...</AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertDescription>Only administrators can create candidate custom attributes.</AlertDescription>
            </Alert>
          )}
          <AttributesDialog
            open={attributeDialogOpen}
            onOpenChange={setAttributeDialogOpen}
            attribute={selectedAttribute}
            onSave={handleDialogSave}
            onCancel={handleDialogCancel}
            mode={dialogMode}
            optionsList={optionsList}
            entityTypes={entityTypes}
            existingNames={attributes
              .filter((a, idx) => dialogMode === "add" || idx !== attrIndex)
              .map((a) => a.name.trim())}
            existingIds={attributes
              .filter((a, idx) => dialogMode === "add" || idx !== attrIndex)
              .map((a) => a.id?.trim() || makeAttributeId(a.name))}
          />
          <Table useDynamicHeight={false}>
            <TableHeader>
              <TableRow className="bg-muted-foreground/5">
                <TableHead>Name</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Required</TableHead>
                <TableHead>Unique</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attributes.map((a, idx) => (
                <TableRow key={idx}>
                  <TableCell>{a.name}</TableCell>
                  <TableCell>
                    <code>{a.id?.trim() || makeAttributeId(a.name)}</code>
                  </TableCell>
                  <TableCell>{a.type}</TableCell>
                  <TableCell>{a.boundaries?.required ? "Yes" : "No"}</TableCell>
                  <TableCell>{a.boundaries?.unique ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Trash2 className="h-4 w-4 cursor-pointer" onClick={() => removeAttribute(idx)} />
                      <EditIcon
                        className="h-4 w-4 cursor-pointer text-green-500"
                        onClick={() => handleEditClick(a, idx)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {update.isError && (
        <Alert variant="destructive">
          <AlertDescription>
            {update.error instanceof Error
              ? update.error.message
              : "Failed to save candidate settings. Please try again."}
          </AlertDescription>
        </Alert>
      )}
      <Button onClick={() => save(attributes, statuses, defaultStatusId, automaticAiMemoryUpdatesEnabled)}>Save</Button>

      <Separator className="my-6" />

      <DeleteConfirmationDialog
        open={attributeToDeleteIndex !== null}
        onOpenChange={(open) => !open && setAttributeToDeleteIndex(null)}
        onConfirm={confirmDeleteAttribute}
        description={`This will permanently delete the attribute "${
          attributeToDeleteIndex !== null ? attributes[attributeToDeleteIndex]?.name : ""
        }". This action cannot be undone.`}
      />
    </div>
  );
}
